import React, { memo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Context } from '@front10/helpers/dist/Context';
import DatePicker from '../DatePicker';
import Box from '../../elements/Box';
import {
  isPastDate,
  isCurrentDay,
  isSameDate,
  isOutsideDay
} from '../../../helpers/dayCard/dayCard';
import ErrorBoundary from '../../utils/ErrorBoundary';

class FareCalendar extends React.Component {
  state = { firstDay: null };

  toMonth = dayjs()
    .add(this.props.months - 1, 'month')
    .toDate();

  componentDidMount = () => {
    let firstDay = dayjs();
    const initialMonth = dayjs(this.props.initialMonth).startOf('month');
    if (dayjs(initialMonth).isValid() && dayjs(initialMonth).isAfter(firstDay)) {
      firstDay = dayjs(initialMonth);
    }
    this.setState({ firstDay: firstDay.toDate() });
  };

  onSelectNavMonth = (item) => {
    const { onSelectMonth, initialMonth } = this.props;
    const date = dayjs().set('month', item.month).set('year', item.year);
    const start =
      dayjs(date).diff(dayjs(initialMonth).startOf('month'), 'month') === 0
        ? initialMonth
        : date.startOf('month').toDate();
    onSelectMonth({ start, end: date.endOf('month').toDate() });
  };

  renderDay = (day, isFirstDay, isLastDay) => {
    const {
      dayCard,
      data,
      loading,
      DayComponentInstance,
      isDesktop,
      usedErrorBoundary,
      currentMonth,
      disregardedBehavior
    } = this.props;
    const { firstDay } = this.state;
    const { direction, translate } = this.context;
    let fare = data.filter(
      (item) => dayjs(day).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY')
    )[0];
    const isLowest = fare ? fare.isLowest : null;
    const isToday = isCurrentDay(day);
    const isOutside = isOutsideDay(day, currentMonth);
    fare = fare ? fare.fareItem : null;
    const style = Object.assign({}, dayCard.style);
    if (isFirstDay) {
      if (direction === 'ltr') {
        style.marginLeft = '0px';
      } else {
        style.marginRight = '0px';
      }
    }
    if (isLastDay) {
      if (direction === 'ltr') {
        style.marginRight = '0px';
      } else {
        style.marginLeft = '0px';
      }
    }

    const isDisregarded =
      dayjs(dayjs(day).format('MM/DD/YYYY')).isBefore(
        dayjs(dayjs(currentMonth).format('MM/DD/YYYY'))
      ) &&
      (dayjs(dayjs(day).format('MM/DD/YYYY')).isAfter(dayjs(dayjs().format('MM/DD/YYYY'))) ||
        dayjs(dayjs(day).format('MM/DD/YYYY')).isSame(dayjs(dayjs().format('MM/DD/YYYY'))));

    const dayRender = (
      <Box className={isDesktop ? 'fr-day-card-container' : ''}>
        <DayComponentInstance
          {...dayCard}
          day={day}
          isOutside={isOutside}
          isLowest={isLowest}
          isToday={isToday}
          isDisregarded={isDisregarded}
          isFirstDay={isSameDate(day, currentMonth)}
          ariaLabel={
            isSameDate(day, firstDay)
              ? translate(
                  'cmp-low-calendar-container-explain',
                  'This is a calendar with the cheapest prices per day'
                )
              : null
          }
          data={fare}
          loading={loading}
          style={style}
          hover={
            isPastDate(day) || (isDisregarded && disregardedBehavior === 'past-date')
              ? 'none'
              : dayCard.hover
          }
          disregardedBehavior={disregardedBehavior}
        />
      </Box>
    );

    return usedErrorBoundary ? <ErrorBoundary>{dayRender}</ErrorBoundary> : { dayRender };
  };

  render = () => {
    const {
      NavComponentInstance,
      months,
      navComponent,
      datePicker,
      usedErrorBoundary,
      initialMonth,
      currentMonth,
      renderNavInfo
    } = this.props;
    const fareCalendarRender = (
      <Box width="100" className="fr-fare-calendar">
        {NavComponentInstance && (
          <NavComponentInstance
            months={months}
            fromMonth={initialMonth}
            {...navComponent}
            onSelect={this.onSelectNavMonth}
            currentMonth={currentMonth}
          />
        )}
        {renderNavInfo}
        <DatePicker
          {...datePicker}
          captionElement={() => null}
          canChangeMonth={false}
          month={currentMonth}
          fromMonth={initialMonth}
          toMonth={this.toMonth}
          renderDay={this.renderDay}
        />
      </Box>
    );

    return usedErrorBoundary ? (
      <ErrorBoundary>{fareCalendarRender}</ErrorBoundary>
    ) : (
      { fareCalendarRender }
    );
  };
}

FareCalendar.propTypes = {
  /**
   * Component to show in navbar
   */
  NavComponentInstance: PropTypes.func.isRequired,
  /**
   * Component to show in cad
   */
  DayComponentInstance: PropTypes.func.isRequired,
  /**
   * Months to show
   */
  months: PropTypes.number,
  /**
   * NavComponentInstance styles props
   */
  navComponent: PropTypes.objectOf(PropTypes.any),
  /**
   * dayCard component props
   */
  renderNavInfo: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),

  dayCard: PropTypes.objectOf(PropTypes.any),
  /**
   * Array of fares to show in calendar
   */
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * Fires when month selected is changed
   */
  onSelectMonth: PropTypes.func,
  /**
   * Define if conponent is loading
   */
  loading: PropTypes.bool,
  /**
   * DatePicker settings
   */
  datePicker: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if is desktop
   */
  isDesktop: PropTypes.bool,
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Define the initial month of the calendar
   */
  initialMonth: PropTypes.instanceOf(Date),
  currentMonth: PropTypes.instanceOf(Date),
  disregardedBehavior: PropTypes.oneOf(['blank-date', 'past-date', 'glass-date'])
};
FareCalendar.defaultProps = {
  months: 3,
  navComponent: {},
  dayCard: {},
  data: [],
  onSelectMonth: () => {},
  renderNavInfo: null,
  loading: false,
  usedErrorBoundary: true, // By default all components protect themselves
  datePicker: {},
  isDesktop: true,
  initialMonth: dayjs().toDate(),
  currentMonth: dayjs().toDate(),
  disregardedBehavior: 'past-date'
};

FareCalendar.contextType = Context;

FareCalendar.componentName = 'FareCalendar';

export default memo(
  FareCalendar,
  (prevProps, nextProps) =>
    prevProps.data === nextProps.data &&
    prevProps.loading === nextProps.loading &&
    prevProps.renderNavInfo === nextProps.renderNavInfo
);
